import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppSettingsContext = createContext({});
const SETTINGS_KEY = 'faristaa_app_settings';

export function AppSettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); } catch { return {}; }
  });

  // Stable reference — won't cause useEffect to re-run
  const fetchSettings = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_API_URL || '/api';
      const r    = await fetch(`${base}/admin/public-settings`);
      if (!r.ok) return; // server error — keep cached settings, don't update
      const data = await r.json();
      const s    = data?.settings || {};
      setSettings(s);
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
    } catch {
      // Offline or network error — keep cached settings silently
    }
  }, []); // empty deps — function never changes

  useEffect(() => {
    fetchSettings();
    window.addEventListener('online', fetchSettings);
    return () => window.removeEventListener('online', fetchSettings);
  }, [fetchSettings]); // stable dep — only runs once

  // Real-time logo update via socket event
  useEffect(() => {
    const onLogoUpdate = (e) => {
      const newLogo = e.detail?.appLogo;
      if (newLogo) {
        setSettings(prev => {
          const updated = { ...prev, appLogo: newLogo };
          localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
          return updated;
        });
      }
    };
    window.addEventListener('faristaa:logo-updated', onLogoUpdate);
    return () => window.removeEventListener('faristaa:logo-updated', onLogoUpdate);
  }, []);

  return (
    <AppSettingsContext.Provider value={settings}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings() {
  return useContext(AppSettingsContext);
}
